import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getAuthToken, removeAuthToken, getQueryFn } from "@/lib/api";
import { User } from "@/types/survey";

type UserResponse = User | { user: User };

function unwrapUser(data: UserResponse): User {
  return "user" in data ? data.user : data;
}

export function useAuth() {
  const queryClient = useQueryClient();
  const token = getAuthToken();
  const hasToken = !!token;

  const { data, isLoading, isError, error, refetch } = useQuery<User | null>({
    queryKey: ["/api/auth/user"],
    enabled: hasToken,
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: async (ctx) => {
      const res = await getQueryFn<UserResponse>({ on401: "throw" })(ctx);
      return res ? unwrapUser(res) : null;
    },
  });

  useEffect(() => {
    if (!hasToken || !isError) return;

    const status =
      Number((error as any)?.status) ||
      Number((error as any)?.message?.match(/^(\d{3}):/)?.[1]);

    if (status === 401 || status === 403) {
      // 1) Stop anything in flight
      queryClient.cancelQueries({ queryKey: ["/api/auth/user"] });

      // 2) Clear local auth
      removeAuthToken();

      // 3) Clear cached user immediately
      queryClient.setQueryData(["/api/auth/user"], null);

      // 4) Remove the query record completely
      queryClient.removeQueries({ queryKey: ["/api/auth/user"] });
    }
  }, [hasToken, isError, error, queryClient]);

  const user = hasToken ? data ?? null : null;

  return {
    user,
    isLoading: hasToken ? isLoading : false,
    isAuthenticated: hasToken && !!user,
    refetch,
  };
}
